// pages/PersonalData/components/Step2Emails/Step2Emails.tsx
import React, { useState, useEffect } from 'react';
import { PersonalData } from "features/personal-data";
import DataList from '../DataList/DataList';
import Button from 'shared/ui/Button/Button';
import { Button_Style, Button_Type } from 'shared/ui/Button/Button';
import { ReactComponent as IconAdd } from 'assets/img/icons/icon_Plus.svg';
import { ReactComponent as IconInfo } from 'assets/img/icons/icon_Info.svg';
import './Step2Emails.scss';

interface Step2EmailsProps {
  existingData: PersonalData[];
  onAdd: (value: string) => Promise<void>;
  onUpdate: (id: number, value: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
}

const Step2Emails: React.FC<Step2EmailsProps> = ({
                                                   existingData,
                                                   onAdd,
                                                   onUpdate,
                                                   onDelete,
                                                   onNext,
                                                   onSkip,
                                                   onBack
                                                 }) => {
  const [newEmail, setNewEmail] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [showForm, setShowForm] = useState(existingData.length === 0);

  // Validation en temps réel
  useEffect(() => {
    if (!newEmail) {
      setIsValid(true);
      setErrorMessage('');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = emailRegex.test(newEmail);

    setIsValid(isValidEmail);
    setErrorMessage(isValidEmail ? '' : 'Format d\'email invalide');
  }, [newEmail]);

  const handleAddEmail = async () => {
    if (!newEmail || !isValid) return;

    setIsAdding(true);
    try {
      await onAdd(newEmail);
      setNewEmail('');
      setShowForm(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddAnother = () => {
    setShowForm(true);
  };

  const canProceed = existingData.length > 0; // Au moins un email valide

  return (
    <div className="step2-emails">
      <div className="step2-emails__header">
        <h2 className="step2-emails__title">
          Protégez vos adresses email
        </h2>
        <p className="step2-emails__subtitle">
          Ajoutez les adresses email que vous souhaitez surveiller
        </p>
      </div>

      {/* Liste des emails existants */}
      {existingData.length > 0 && (
        <div className="step2-emails__list">
          <h3 className="step2-emails__list-title">
            Vos emails protégés
          </h3>
          <DataList
            items={existingData}
            type="email"
            onEdit={onUpdate}
            onDelete={onDelete}
          />
        </div>
      )}

      {/* Formulaire d'ajout */}
      {showForm ? (
        <div className="step2-emails__form">
          <h3 className="step2-emails__form-title">
            {existingData.length === 0
              ? 'Ajoutez votre premier email'
              : 'Ajouter un nouvel email'}
          </h3>

          <div className="step2-emails__input-group">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="exemple@email.com"
              className={`
                                step2-emails__input
                                ${!isValid && newEmail ? 'step2-emails__input--error' : ''}
                            `}
              autoFocus
            />

            {errorMessage && (
              <span className="step2-emails__error">{errorMessage}</span>
            )}
          </div>

          <div className="step2-emails__form-actions">
            <Button
              label="Ajouter"
              type={Button_Type.PRIMARY}
              style={Button_Style.SOLID}
              onClick={handleAddEmail}
              disabled={!newEmail || !isValid || isAdding}
              className="step2-emails__add-btn"
            />

            {existingData.length > 0 && (
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="step2-emails__cancel-btn"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      ) : (
        // Bouton pour ajouter un autre email
        <div className="step2-emails__add-more">
          <button
            type="button"
            onClick={handleAddAnother}
            className="step2-emails__add-more-btn"
          >
            <IconAdd className="step2-emails__add-more-icon" />
            <span>Ajouter un autre email</span>
          </button>
        </div>
      )}

      {/* Message d'information */}
      {!canProceed && (
        <div className="step2-emails__info">
          <IconInfo className="step2-emails__info-icon" />
          <span className="step2-emails__info-text">
                        Ajoutez au moins un email pour continuer
                    </span>
        </div>
      )}

      {/* Actions de navigation */}
      <div className="step2-emails__navigation">
        <Button
          label="Retour"
          type={Button_Type.SECONDARY}
          style={Button_Style.OUTLINED}
          onClick={onBack}
          className="step2-emails__nav-btn"
        />

        <div className="step2-emails__nav-right">
          <Button
            label="Passer"
            type={Button_Type.SECONDARY}
            style={Button_Style.OUTLINED}
            onClick={onSkip}
            className="step2-emails__nav-btn"
          />

          <Button
            label="Continuer"
            type={Button_Type.PRIMARY}
            style={Button_Style.SOLID}
            onClick={onNext}
            disabled={!canProceed}
            className="step2-emails__nav-btn step2-emails__nav-btn--primary"
          />
        </div>
      </div>
    </div>
  );
};

export default Step2Emails;